import { Alert, AlertProps } from "@jugl-web/ui-components/cross-platform";
import React, { FC, useMemo } from "react";
import { SubscriptionPlanModuleId } from "@jugl-web/rest-api";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import clientsLimitationAnimation from "./assets/clients-limitation-animation.json";
import workatoImage from "./assets/workato-image.png";
import { ReactComponent as IntegrationsIcon } from "./assets/integrations.svg";

export type ModuleNotAvailableAlertProps = {
  isOpen: boolean;
  onRequestClose: () => void;
  module: SubscriptionPlanModuleId | `${SubscriptionPlanModuleId}`;
  entityId: string;
  isAdmin: boolean;
};

export const ModuleNotAvailableAlert: FC<ModuleNotAvailableAlertProps> = ({
  isOpen,
  onRequestClose,
  module,
  entityId,
  isAdmin,
}) => {
  const { navigateToPage } = useNavigation();
  const {
    title,
    header,
    animation,
    content,
    img,
    className,
    buttonsContainerClassName,
    isCloseButtonVisible,
  } = useMemo<
    Pick<
      AlertProps,
      | "buttonsContainerClassName"
      | "className"
      | "header"
      | "title"
      | "content"
      | "isCloseButtonVisible"
    > & {
      img?: string;
      animation?: unknown;
    }
  >(() => {
    switch (module) {
      case "Jugl::Clients":
        return {
          title: "Jugl Customer Management",
          animation: clientsLimitationAnimation,
          content:
            "Convenient Customer base, for easy management and communication with Customers.",
        };
      case "Jugl::Integration":
        return {
          header: (
            <div className="flex items-center gap-3">
              <IntegrationsIcon />
              <span>Integrations</span>
            </div>
          ),
          img: workatoImage,
          title: "Automate your work with Integrations 🚀",
          content: (
            <>
              <div>
                More than 200+ Apps to make your workflow easy. Create
                connections and receipts with your favourite Apps using Jugl
                Integrations! 😍
              </div>
              {isAdmin && (
                <div className="mt-4 text-primary-900">
                  To enable Integrations module, please email us on{" "}
                  <a
                    href="mailto:support@jugl.com"
                    className="text-inherit underline"
                  >
                    support@jugl.com
                  </a>{" "}
                  🙌
                </div>
              )}
            </>
          ),
          isCloseButtonVisible: true,
          buttonsContainerClassName: "w-[200px] mx-auto",
          className: "max-w-[760px] w-full",
        };
      default:
        return {
          title: "Pro Feature",
          content: "This feature is not available in your current plan",
        };
    }
  }, [module, isAdmin]);

  return (
    <Alert
      isOpen={isOpen}
      buttonsContainerClassName={buttonsContainerClassName}
      onRequestClose={onRequestClose}
      className={className}
      header={header || "Upgrade the Plan"}
      title={title}
      isCloseButtonVisible={isCloseButtonVisible}
      img={(() => {
        if (animation) {
          return <Lottie options={{ animationData: animation }} width={250} />;
        }

        if (img) {
          return <img src={img} alt={title} className="w-full" />;
        }

        return null;
      })()}
      content={
        <div className="flex flex-col gap-2 text-center">
          <div>{content}</div>
          {module !== "Jugl::Integration" && (
            <>
              <div className="font-medium text-primary-800">
                Available to Pro or Enterprise plans
              </div>
              {!isAdmin && (
                <div className="text-sm text-dark-600">
                  To update the Plan, please reach out to Admin of the Workspace
                </div>
              )}
            </>
          )}
        </div>
      }
      buttons={[
        isAdmin && module !== "Jugl::Integration"
          ? {
              text: "Upgrade",
              onClick: () => {
                onRequestClose();
                navigateToPage("workspaceSelectSubscription", undefined, {
                  entityId,
                });
              },
            }
          : {
              text: "Got it",
              role: "close",
              color: "primary",
            },
      ]}
    />
  );
};
